import { logger } from '@/core/logger';
/**
 * initService – centralized app/library initialization runner
 *
 * Why
 * - Splash must wait at least N ms AND until all libraries finish initializing
 * - Future libraries can register async init tasks without touching Splash
 *
 * API (quick start)
 * 1) Register a task (anywhere during app boot, before Splash decides to navigate):
 *    registerInitTask('react-query', async () => {
 *      // e.g., warm up caches, prefetch, etc.
 *    }, { priority: 'high', timeoutMs: 15000, retries: 1, critical: false });
 *
 * 2) Splash calls runInitTasks() once at startup (handled by useSplash)
 *    - runInitTasks returns a summary and NEVER throws; errors are captured in the summary
 *    - tasks run respecting dependencies/priority with bounded concurrency
 *
 * Options
 * - priority: 'high' | 'normal' | 'low' (default 'normal')
 * - timeoutMs: per-task timeout (default 30s)
 * - retries: retry count on failure with linear backoff (default 0)
 * - dependsOn: array of other task names which must finish first
 * - critical: mark failure as degraded system (still resolves overall)
 *
 * Testing/Dev helpers
 * - setInitConcurrency(n): set parallelism
 * - resetInitRegistry(): clear registry (tests)
 *
 * Notes
 * - Duplicate task names: last registration wins (warns)
 * - Cycles in dependsOn are detected and logged; tasks in a cycle will be skipped
 */

type InitTask = {
    name: string;
    task: () => Promise<void>;
    priority: 'high' | 'normal' | 'low';
    timeoutMs: number;
    retries: number;
    dependsOn: string[];
    critical: boolean;
};

type InitFailure = { name: string; error: string; attempts: number; elapsedMs: number };

export type InitSummary = {
    total: number;
    succeeded: number;
    failed: number;
    durationMs: number;
    failures: InitFailure[];
    degraded: boolean;
};

const registry = new Map<string, InitTask>();
let hasRun = false;
let runningPromise: Promise<InitSummary> | null = null;
let defaultConcurrency = Math.max(2, Math.floor((typeof navigator !== 'undefined' ? 4 : 4) / 2));

export function setInitConcurrency(limit: number): void {
    defaultConcurrency = Math.max(1, Math.floor(limit));
}

export function resetInitRegistry(): void {
    registry.clear();
    hasRun = false;
    runningPromise = null;
}

export function registerInitTask(
    name: string,
    task: () => Promise<void>,
    options?: {
        priority?: 'high' | 'normal' | 'low';
        timeoutMs?: number;
        retries?: number;
        dependsOn?: string[];
        critical?: boolean;
    }
): void {
    const normalized: InitTask = {
        name,
        task,
        priority: options?.priority ?? 'normal',
        timeoutMs: options?.timeoutMs ?? 30_000,
        retries: options?.retries ?? 0,
        dependsOn: options?.dependsOn ?? [],
        critical: options?.critical ?? false,
    };

    if (registry.has(name)) {
        logger.warn('initService', `Duplicate init task '${name}' registered. Overwriting previous task.`);
    }
    registry.set(name, normalized);
}

function sortByPriority(tasks: InitTask[]): InitTask[] {
    const weight: Record<InitTask['priority'], number> = { high: 0, normal: 1, low: 2 };
    return [...tasks].sort((a, b) => weight[a.priority] - weight[b.priority]);
}

function detectCycles(): string[] {
    // Simple cycle detection using DFS
    const visited = new Set<string>();
    const stack = new Set<string>();
    const cycles: string[] = [];

    const visit = (name: string) => {
        if (stack.has(name)) {
            cycles.push(name);
            return;
        }
        if (visited.has(name)) return;
        visited.add(name);
        stack.add(name);
        const t = registry.get(name);
        if (t) {
            t.dependsOn.forEach(visit);
        }
        stack.delete(name);
    };

    registry.forEach((_t, name) => visit(name));
    return [...new Set(cycles)];
}

async function withTimeout<T>(p: Promise<T>, ms: number, name: string): Promise<T> {
    let to: any;
    const timeout = new Promise<T>((_, reject) => {
        to = setTimeout(() => reject(new Error(`Task '${name}' timed out after ${ms}ms`)), ms);
    });
    try {
        const result = await Promise.race([p, timeout]);
        return result as T;
    } finally {
        clearTimeout(to);
    }
}

async function runWithRetry(task: InitTask): Promise<{ ok: boolean; attempts: number; error?: string; elapsedMs: number }> {
    let attempt = 0;
    const start = Date.now();
    while (attempt <= task.retries) {
        try {
            attempt++;
            await withTimeout(task.task(), task.timeoutMs, task.name);
            return { ok: true, attempts: attempt, elapsedMs: Date.now() - start };
        } catch (e) {
            const backoff = Math.floor(300 * Math.pow(1.5, attempt - 1));
            if (attempt > task.retries) {
                return { ok: false, attempts: attempt, error: e instanceof Error ? e.message : String(e), elapsedMs: Date.now() - start };
            }
            await new Promise((res) => setTimeout(res, backoff));
        }
    }
    return { ok: false, attempts: attempt, error: 'Unknown failure', elapsedMs: Date.now() - start };
}

function topoBatches(tasks: InitTask[]): InitTask[][] {
    // Build dependency counts
    const nameToTask = new Map(tasks.map((t) => [t.name, t] as const));
    const indeg = new Map<string, number>();
    tasks.forEach((t) => indeg.set(t.name, 0));
    tasks.forEach((t) => t.dependsOn.forEach((d) => indeg.set(t.name, (indeg.get(t.name) ?? 0) + 1)));

    const ready: InitTask[] = tasks.filter((t) => (indeg.get(t.name) ?? 0) === 0);
    const batches: InitTask[][] = [];
    const queue = sortByPriority(ready);

    const remainingDeps = new Map<string, Set<string>>();
    tasks.forEach((t) => remainingDeps.set(t.name, new Set(t.dependsOn)));

    while (queue.length) {
        const batch: InitTask[] = [];
        // take all currently ready tasks as a batch
        while (queue.length) batch.push(queue.shift()!);
        batches.push(batch);

        // simulate removing batch from the graph
        batch.forEach((done) => {
            tasks.forEach((t) => {
                const deps = remainingDeps.get(t.name)!;
                if (deps.has(done.name)) {
                    deps.delete(done.name);
                    if (deps.size === 0) {
                        queue.push(t);
                    }
                }
            });
        });
        // keep priority order for the next batch
        if (queue.length) {
            const dedup = Array.from(new Set(queue.map((t) => t.name))).map((n) => nameToTask.get(n)!)
                .filter(Boolean);
            queue.length = 0;
            sortByPriority(dedup).forEach((t) => queue.push(t));
        }
    }

    return batches;
}

export async function runInitTasks(opts?: { concurrency?: number }): Promise<InitSummary> {
    if (hasRun && runningPromise) return runningPromise;
    hasRun = true;

    const run = async (): Promise<InitSummary> => {
        const allTasks = Array.from(registry.values());
        const startAll = Date.now();
        const failures: InitFailure[] = [];
        let succeeded = 0;
        let degraded = false;

        if (allTasks.length === 0) {
            logger.info('initService', 'No init tasks registered');
            return { total: 0, succeeded: 0, failed: 0, durationMs: 0, failures: [], degraded: false };
        }

        const cycles = detectCycles();
        if (cycles.length) {
            logger.error('initService', `Dependency cycle detected in init tasks: ${cycles.join(', ')}`);
        }

        // Build batches with dependencies considered
        const batches = topoBatches(allTasks);
        const concurrency = Math.max(1, opts?.concurrency ?? defaultConcurrency);

        for (const batch of batches) {
            // Run tasks in this batch with bounded concurrency
            const queue = [...batch];
            const workers: Promise<void>[] = [];
            const runWorker = async () => {
                while (queue.length) {
                    const t = queue.shift()!;
                    logger.info('initService', `Starting init task: ${t.name}`);
                    const res = await runWithRetry(t);
                    if (res.ok) {
                        succeeded++;
                        logger.info('initService', `Completed init task: ${t.name} in ${res.elapsedMs}ms (attempts: ${res.attempts})`);
                    } else {
                        failures.push({ name: t.name, error: res.error ?? 'Unknown error', attempts: res.attempts, elapsedMs: res.elapsedMs });
                        logger.error('initService', `Init task failed: ${t.name} after ${res.elapsedMs}ms (attempts: ${res.attempts}) - ${res.error}`);
                        if (t.critical) degraded = true;
                    }
                }
            };
            for (let i = 0; i < Math.min(concurrency, queue.length); i++) {
                workers.push(runWorker());
            }
            await Promise.all(workers);
        }

        const durationMs = Date.now() - startAll;
        return {
            total: allTasks.length,
            succeeded,
            failed: failures.length,
            durationMs,
            failures,
            degraded,
        };
    };

    runningPromise = run();
    return runningPromise;
}

/**
 * HOW TO ADD INIT TASKS
 * =====================
 * 
 * Register an init task that runs during app startup (splash screen).
 * Tasks run in parallel with bounded concurrency, respecting dependencies and priority.
 * 
 * Basic Example:
 * ```ts
 * import { registerInitTask } from '@/core/services/initService';
 * 
 * registerInitTask(
 *   'my-feature:init',
 *   async () => {
 *     // Your async initialization code here
 *     await setupMyFeature();
 *   },
 *   { 
 *     priority: 'high',        // 'high' | 'normal' | 'low' (default: 'normal')
 *     timeoutMs: 15000,        // Per-task timeout (default: 30000)
 *     retries: 1,              // Retry count on failure (default: 0)
 *     dependsOn: ['i18n:preload'], // Other tasks that must finish first
 *     critical: false          // Mark as critical for degraded system detection
 *   }
 * );
 * ```
 * 
 * Priority Levels:
 * - 'high': Runs first, blocks splash screen
 * - 'normal': Runs after high-priority tasks
 * - 'low': Runs last, doesn't block critical initialization
 * 
 * Dependencies:
 * - Use `dependsOn` to ensure tasks run in order
 * - Example: A task that needs translations should depend on 'i18n:preload'
 * 
 * Notes:
 * - Tasks run in parallel where possible (bounded concurrency)
 * - Failures are logged but don't crash the app
 * - Critical task failures mark the system as "degraded"
 * - Each task has its own timeout and retry logic
 */

// i18n preload init task – ensures both English and Arabic translations are ready during splash
// This task preloads BOTH locales to ensure smooth language switching
try {
    // Lazy import to avoid circular dependency at module load
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { preloadTranslations } = require('@/core/services/translationService');
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { initializeI18n } = require('@/core/utils/i18n');

    registerInitTask(
        'i18n:preload',
        async () => {
            // Get the current user language preference
            const lang = await initializeI18n();
            const otherLang = lang === 'en' ? 'ar' : 'en';

            // Preload BOTH locales in parallel for optimal performance
            // This ensures both English and Arabic translations are ready during splash
            // Current language takes priority (for immediate UI), but both are fetched together
            logger.info('initService', `Preloading translations for ${lang} and ${otherLang} in parallel`);

            await Promise.allSettled([
                preloadTranslations(lang), // Current language (critical)
                preloadTranslations(otherLang), // Other language (for smooth switching)
            ]);

            logger.info('initService', `Completed preloading translations for both locales`);
        },
        { priority: 'high', timeoutMs: 20000, retries: 1, critical: false }
    );
} catch {
    logger.warn('initService', 'i18n preload task registration skipped');
}


